import React, { useEffect, useRef } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { useTable, useSortBy, useFlexLayout } from 'react-table';
import {
  TableColumnDefinitions,
  TableHeaderCellType,
} from '~/components/organism/Table/types';
import { FormListData } from './utils/composeData';
import Catalog from '~/Catalog';
import { ACTION_COLUMN_ACCESSOR } from '~/components/organism/Table/components/Header/utils/asActionCell';
import { isInitialLoadStatus } from '~/graphql/ApolloConstants';
import TEST_ID from './index.testid';
import Row from '~/components/organism/Table/components/Row';
import HeaderLarge from '~/components/organism/Table/components/Header/components/Large';
import LoadingBody from '~/components/organism/Table/components/LoadingBody';
import BodyOuter from '~/components/organism/Table/components/Body/components/Outer';
import BodyInner from '~/components/organism/Table/components/Body/components/Inner';
import useSortSettings from '~/hooks/useSortSettings';
import { MAIN_CONTENT_WIDTH } from '~/components/organism/NavigationFrame/constants';
import NoResultSection from '~/components/page/NoResultSection';
import FormActionColumnCell from './components/ActionColumn';
import useViewportSize from '~/hooks/useViewportSize';
import { FORM_SORT_SETTINGS } from '~/util/localStorageKeys';
import HeaderCell from '~/components/organism/Table/components/Header/components/HeaderCell';
import RowCell from '~/components/organism/Table/components/Row/components/RowCell';
import CheckboxColumnCell from '~/components/organism/Table/components/CheckboxColumnCell';
import type { ReturnProps } from '~/components/organism/Table/hooks/useRowSelect';
import { CHECKBOX_COLUMN_ACCESSOR } from '~/components/organism/Table/components/Header/utils/asCheckboxCell';
import { isEmptyObject } from '~/util/object';
import { noop } from 'lodash';
import CheckboxHeaderCell from '~/components/organism/Table/components/Header/components/CheckboxHeaderCell';
import type { SelectedForm } from '../..';

const text = {
  toggleSortBy: '',
  queryError: Catalog.genericUnknownErrorMessage,
  noSearchResults: Catalog.noResults,
  selectAllOnPage: 'Selecteer alles op pagina',
};

const checkboxOptions = [
  {
    key: 'select-all-on-page',
    label: text.selectAllOnPage,
    payload: 'select-all-on-page',
  },
];

type Props = {
  /** Table columns, column grouping limited to at most one level */
  columns: TableColumnDefinitions;

  /** Table data*/
  data: Array<FormListData>;
  networkStatus: number;
  loading: boolean;
  error: boolean;
  refreshTable: () => void;
  options?: {
    renderHeaderCell?: (
      key: string,
      column: TableHeaderCellType<any>,
      isLastColumn: boolean,
    ) => React.ReactNode;
  };
  selectionProps?: ReturnProps<SelectedForm>;
};

const FormListTable: React.FCC<Props> = ({
  columns,
  data,
  error,
  loading,
  refreshTable,
  options,
  networkStatus,
  selectionProps = {
    itemsSelected: [],
    allSelected: false,
    selectAllItemsOnPage: f => f,
    toggleRowSelect: () => {},
    checkIfSelected: () => false,
    selectAllItems: () => {},
    deselectAllItems: () => {},
  },
  ...rest
}) => {
  const theme = useTheme();
  const [sortSettings] = useSortSettings<Array<string>>(FORM_SORT_SETTINGS);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setHiddenColumns,
    toggleHideAllColumns,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      disableMultiSort: true,
      autoResetSortBy: false,
      autoResetHiddenColumns: false,
      getRowId: ({ id }) => id,
      manualSortBy: true,
      disableSortRemove: true,
      initialState: {
        sortBy: [{ id: sortSettings.id[0], desc: sortSettings.desc }],
      },
    },
    useFlexLayout,
    useSortBy,
  );

  const {
    checkIfSelected = () => false,
    toggleRowSelect = noop,
    allSelected = false,
    selectAllItemsOnPage = (f, z) => ({ f, z }),
    itemsSelected = [],
    deselectAllItems = noop,
  } = selectionProps;
  const isSelectable = !isEmptyObject(selectionProps);

  const headerCheckboxRef = useRef<HTMLDivElement | null>(null);
  const oneCheckboxSelected = rows.some(row =>
    checkIfSelected({ formId: row.id, formName: row.original.name }),
  );

  const viewportSize = useViewportSize();
  useEffect(() => {
    if (viewportSize.width > MAIN_CONTENT_WIDTH) {
      toggleHideAllColumns(false);
    } else {
      setHiddenColumns(['formDescription', 'lastModified', 'createdDate']);
    }
  }, [viewportSize.width, setHiddenColumns, toggleHideAllColumns, theme]);

  let bodyComponent;
  if (error) {
    bodyComponent = (
      <StyledNoResultSection data-testid={TEST_ID.ERROR_MESSAGE}>
        {text.queryError}
      </StyledNoResultSection>
    );
  } else if (isInitialLoadStatus(networkStatus)) {
    bodyComponent = (
      <LoadingBody
        rowAmount={rows.length || 10}
        getTableBodyProps={getTableBodyProps}
      />
    );
  } else if (data.length === 0 && !loading) {
    bodyComponent = (
      <StyledNoResultSection>{text.noSearchResults}</StyledNoResultSection>
    );
  } else {
    bodyComponent = (
      <BodyInner {...getTableBodyProps()}>
        {rows.map(row => {
          prepareRow(row);

          const rowProps = { ...row.getRowProps() };
          const id = row.original.id;
          const name = row.original.name;
          const description = row.original.description;
          const isDeleted = row.original && row.original.isDeleted === true;
          const isRowSelected = checkIfSelected({ formId: id, formName: name });

          return (
            <Row
              {...rowProps}
              isDeleted={isDeleted}
              key={rowProps.key}
              data-objectid={id}
              data-deleted={isDeleted}
              dataTestId={TEST_ID.LIST_ITEM}
              to={`/-/forms/${id}`}
            >
              {row.cells.map((cell, idx) => {
                if (cell.column.id === ACTION_COLUMN_ACCESSOR) {
                  return (
                    <FormActionColumnCell
                      key={`cell-${idx}`}
                      name={name}
                      description={description}
                      id={id}
                      shared={row.original.shared}
                      enabled={row.original.enabled}
                      cell={cell}
                      refreshTable={refreshTable}
                    />
                  );
                }

                if (
                  cell.column.id === CHECKBOX_COLUMN_ACCESSOR &&
                  isSelectable
                ) {
                  return (
                    <CheckboxColumnCell
                      key={`cell-${idx}`}
                      cell={cell}
                      onChange={() =>
                        toggleRowSelect({ formId: id, formName: name })
                      }
                      value={allSelected ? true : isRowSelected}
                      disabled={allSelected}
                    />
                  );
                }

                return <RowCell key={`cell-${idx}`} cell={cell} />;
              })}
            </Row>
          );
        })}
      </BodyInner>
    );
  }

  return (
    <Container {...getTableProps()} {...rest} data-testid={TEST_ID.CONTAINER}>
      <HeaderLarge>
        {headerGroups.map(headerGroup => {
          const headerGroupProps = {
            ...headerGroup.getHeaderGroupProps(),
          };
          return (
            <StyledHeaderTr
              key={headerGroupProps.key}
              data-testid={TEST_ID.HEADER_ROW}
            >
              {headerGroup.headers.map((column, idx) => {
                const isLastColumn = idx === headerGroup.headers.length - 1;
                const headerCellKey = `header-${idx}`;

                if (options && options.renderHeaderCell) {
                  return options.renderHeaderCell(
                    headerCellKey,
                    column,
                    isLastColumn,
                  );
                }
                if (column.id === CHECKBOX_COLUMN_ACCESSOR) {
                  return (
                    <CheckboxHeaderCell
                      key={`header-cell-${idx}`}
                      ref={headerCheckboxRef}
                      column={column}
                      minus={oneCheckboxSelected || allSelected}
                      value={allSelected ? true : oneCheckboxSelected}
                      noRows={rows.length === 0}
                      options={checkboxOptions}
                      onChange={
                        itemsSelected.length > 0 ? deselectAllItems : undefined
                      }
                      onOptionChange={({ option }) => {
                        switch (option.payload) {
                          case 'select-all-on-page':
                            selectAllItemsOnPage(
                              data.map(item => ({
                                formId: item.id,
                                formName: item.name,
                              })),
                              !oneCheckboxSelected,
                            );
                            break;
                        }
                      }}
                    />
                  );
                }

                return (
                  <HeaderCell
                    key={headerCellKey}
                    column={column}
                    isLastColumn={isLastColumn}
                  />
                );
              })}
            </StyledHeaderTr>
          );
        })}
      </HeaderLarge>
      <BodyOuter>{bodyComponent}</BodyOuter>
    </Container>
  );
};

const StyledNoResultSection = styled(NoResultSection)<{}>`
  max-width: 800px;
`;

const StyledHeaderTr = styled.div<{}>`
  white-space: nowrap;
  display: flex;
`;

type TableWithStyleProps = {
  onClick?: () => void;
};
const Container = styled.div<TableWithStyleProps>(
  ({ onClick, theme }) => css`
    cursor: ${onClick ? 'pointer' : 'default'};
    user-select: none;
    border-spacing: 0;
    border-radius: ${theme.getTokens().border.radius.m};
    border: 1px solid ${theme.color('tertiary', 'light')};
    margin-bottom: ${theme.space('l')};
    box-shadow: ${theme.boxShadow('card')};
  `,
);

const defaultColumn = {
  minWidth: 20,
  width: 120,
  maxWidth: 200,
};

export default FormListTable;
